import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Trophy, Calendar, Users, Clock, Music, ArrowLeft, Star, Mic, ChevronRight, ChevronDown, ChevronUp, Play, Heart, Activity, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, Clock4, Swords, Crown, Circle as XCircle, Shield, TrendingUp, Mail, Check, X as XMarkIcon } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import TournamentBracket from '../components/TournamentBracket';
import TournamentLeaderboard from '../components/TournamentLeaderboard';
import { TournamentEntriesList } from '../components/TournamentEntriesList';
import MatchCountdown from '../components/MatchCountdown';
import { VirtualCurrencyService } from '../services/VirtualCurrencyService';
import { BracketGenerationService } from '../services/BracketGenerationService';
import { MatchProgressionService, type MatchWithDetails } from '../services/MatchProgressionService';
import { tournamentService, TournamentInvitation, getMyTournamentSongHistory, type TournamentSongHistoryItem } from '../services/TournamentService';

interface Tournament {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'upcoming' | 'active' | 'completed';
  entry_fee: number | null;
  prize_pool_amount: number | null;
  prize_pool_currency: string | null;
  prize_description: string;
  max_participants: number | null;
  participant_count?: number;
  songs?: TournamentSong[];
  allow_pre_registration?: boolean;
  is_featured?: boolean;
  is_shareable?: boolean;
  registration_locked?: boolean;
  locked_participant_count?: number | null;
  locked_at?: string | null;
  registration_close_date?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  logo_url?: string | null;
  prize_display?: string | null;
  background_type?: string | null;
  shader_preset?: string | null;
  sponsor_logos?: string[] | null;
  sponsor_links?: string[] | null;
  background_image_url?: string | null;
}

interface TournamentSong {
  id: string;
  song_id: string;
  tournament_id: string;
  song?: {
    id: string;
    title: string;
    artist: string;
    cover_url: string;
    audio_url: string;
  };
}

interface TournamentEntry {
  id: string;
  tournament_id: string;
  user_id: string;
  performance_id: string | null;
  status: 'registered' | 'submitted' | 'evaluated';
  score: number | null;
  created_at: string;
  updated_at: string | null;
  profile?: {
    username: string;
    full_name: string | null;
    avatar_url: string | null;
  };
  performance?: {
    id: string;
    vocal_recording_url: string;
    score: number | null;
    created_at: string;
    recording?: {
      id: string;
      tournament_round: number | null;
      song?: {
        id: string;
        title: string;
        artist: string;
        cover_url: string | null;
      };
    };
  };
}

export const TournamentDetail: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [entries, setEntries] = useState<TournamentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entryLoading, setEntryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userEntry, setUserEntry] = useState<TournamentEntry | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [currentView, setCurrentView] = useState<'participants' | 'bracket' | 'leaderboard' | 'myEntries'>('participants');
  const [playingEntry, setPlayingEntry] = useState<TournamentEntry | null>(null);
  const [userCurrentRound, setUserCurrentRound] = useState<number>(1);
  const [userEliminated, setUserEliminated] = useState<boolean>(false);
  const [usedSongIds, setUsedSongIds] = useState<Set<string>>(new Set());
  const [currentRoundRecordings, setCurrentRoundRecordings] = useState<TournamentSongHistoryItem[]>([]);
  const [currentMatch, setCurrentMatch] = useState<MatchWithDetails | null>(null);
  const [userMatches, setUserMatches] = useState<MatchWithDetails[]>([]);
  const [bracketTotalRounds, setBracketTotalRounds] = useState<number>(0);
  const [roundPerformances, setRoundPerformances] = useState<any[]>([]);
  const [pendingInvitation, setPendingInvitation] = useState<TournamentInvitation | null>(null);
  const [invitationResponding, setInvitationResponding] = useState(false);

  useEffect(() => {
    if (tournamentId) {
      setTournament(null);
      setEntries([]);
      setUserEntry(null);
      setLoading(true);
      loadTournament();
      loadEntries();
    }
  }, [tournamentId]);

  useEffect(() => {
    if (tournamentId && user) {
      loadEntries();
    }
  }, [user]);

  useEffect(() => {
    if (tournamentId && user?.id) {
      tournamentService.getUserInvitationForTournament(tournamentId, user.id)
        .then(inv => setPendingInvitation(inv?.status === 'pending' ? inv : null))
        .catch(() => setPendingInvitation(null));
    }
  }, [tournamentId, user?.id]);

  useEffect(() => {
    if (tournamentId) {
      MatchProgressionService.getBracketConfig(tournamentId)
        .then(cfg => setBracketTotalRounds(cfg?.totalRounds ?? 0))
        .catch(() => setBracketTotalRounds(0));
    }
  }, [tournamentId]);

  useEffect(() => {
    if (tournamentId && user && userEntry) {
      MatchProgressionService.getCurrentMatch(tournamentId, user.id)
        .then(match => setCurrentMatch(match))
        .catch(() => setCurrentMatch(null));

      MatchProgressionService.getUserMatches(tournamentId, user.id)
        .then(matches => setUserMatches(matches))
        .catch(() => setUserMatches([]));
    } else {
      setCurrentMatch(null);
      setUserMatches([]);
    }
  }, [tournamentId, user, userEntry, userEliminated]);

  const getActualStatus = (dbStatus: string, startDate: string, endDate: string, regLocked?: boolean): 'upcoming' | 'active' | 'completed' => {
    const now = new Date();
    const end = new Date(endDate);
    if (now > end) return 'completed';
    if (dbStatus === 'active' || regLocked) return 'active';
    const start = new Date(startDate);
    if (now >= start) return 'active';
    return 'upcoming';
  };

  const isRegistrationClosed = (t: Tournament | null): boolean => {
    if (!t) return false;
    if (t.registration_locked) return true;
    if (t.registration_close_date) {
      return new Date(t.registration_close_date) < new Date();
    }
    return false;
  };

  const canSubmitRecordings = (t: Tournament | null): boolean => {
    if (!t) return false;
    if (t.status !== 'active') return false;
    return isRegistrationClosed(t);
  };

  const loadTournament = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: tournamentError } = await supabase
        .from('tournaments')
        .select(`
          *,
          songs:tournament_songs (
            id,
            song_id,
            song:songs (
              id,
              title,
              artist,
              cover_url,
              audio_url
            )
          )
        `)
        .eq('id', tournamentId)
        .single();

      if (tournamentError) throw tournamentError;

      // Get participant count
      const { count, error: countError } = await supabase
        .from('tournament_entries')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', tournamentId);

      if (countError) throw countError;

      const actualStatus = getActualStatus(data.status, data.start_date, data.end_date, data.registration_locked);

      setTournament({
        ...data,
        status: actualStatus,
        participant_count: count || 0
      });
    } catch (error) {
      console.error('Error loading tournament:', error);
      setError('Failed to load tournament details.');
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      const { data, error: entriesError } = await supabase
        .from('tournament_entries')
        .select(`
          *,
          profile:profiles (username, full_name, avatar_url),
          performance:performances (
            id,
            vocal_recording_url,
            score,
            created_at,
            recording:recordings (
              id,
              tournament_round,
              song:songs (
                id,
                title,
                artist,
                cover_url
              )
            )
          )
        `)
        .eq('tournament_id', tournamentId)
        .order('score', { ascending: false });

      if (entriesError) throw entriesError;

      setEntries(data || []);

      const { data: roundPerfData } = await supabase
        .from('recordings')
        .select(`
          id,
          user_id,
          tournament_round,
          song_id,
          created_at,
          song:songs (id, title, artist, cover_url),
          performance:performances!performances_recording_id_fkey (
            id,
            score,
            vocal_recording_url,
            created_at,
            analysis:performance_analysis (overall_score)
          ),
          profile:profiles!recordings_user_id_fkey (username, full_name, avatar_url)
        `)
        .eq('tournament_id', tournamentId)
        .not('tournament_round', 'is', null)
        .order('tournament_round', { ascending: true });
      setRoundPerformances(roundPerfData || []);

      // Find user's entry if logged in
      if (user) {
        const userEntryData = data?.find(entry => entry.user_id === user.id) || null;
        setUserEntry(userEntryData);

        // Load user's round info and recordings if they're in the tournament
        if (userEntryData) {
          // Get current round and elimination status from tournament_entries
          setUserCurrentRound(userEntryData.current_round || 1);
          setUserEliminated((userEntryData.current_round === 0) || !!userEntryData.eliminated_at);

          const songHistory = await getMyTournamentSongHistory(tournamentId!);
          setUsedSongIds(new Set(songHistory.map(r => r.song_id)));
          setCurrentRoundRecordings(
            songHistory.filter(r => r.round_number === (userEntryData.current_round || 1))
          );
        }
      }
    } catch (error) {
      console.error('Error loading tournament entries:', error);
    }
  };

  const handleInvitationResponse = async (status: 'accepted' | 'declined') => {
    if (!pendingInvitation) return;
    try {
      setInvitationResponding(true);
      if (status === 'accepted' && user && tournamentId) {
        await tournamentService.acceptInvitationAndJoin(pendingInvitation.id, tournamentId, user.id);
        setPendingInvitation(null);
        await loadEntries();
        await loadTournament();
      } else {
        await tournamentService.respondToInvitation(pendingInvitation.id, status);
        setPendingInvitation(null);
        if (status === 'declined') {
          navigate('/tournaments');
        }
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
    } finally {
      setInvitationResponding(false);
    }
  };

  const handleJoinTournament = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setEntryLoading(true);

      // Check if user already joined
      if (userEntry) {
        return;
      }

      // Check if registration is locked
      if (tournament?.registration_locked) {
        setError('Registration is closed for this tournament.');
        return;
      }

      // Check entry fee and deduct coins if required
      if (tournament?.entry_fee && tournament.entry_fee > 0) {
        const coinService = new VirtualCurrencyService();

        // Get user's current coin balance
        const userCoins = await coinService.getUserCoins(user.id);
        const currentBalance = userCoins?.balance || 0;

        if (currentBalance < tournament.entry_fee) {
          setError(`Insufficient coins. You need ${tournament.entry_fee} coins but only have ${currentBalance}. Please purchase more coins to enter this tournament.`);
          return;
        }

        // Deduct entry fee from user's balance
        try {
          await coinService.deductCoins(
            user.id,
            tournament.entry_fee,
            'tournament_entry',
            `Tournament entry: ${tournament.title}`,
            tournamentId
          );
        } catch (coinError) {
          if (coinError instanceof Error && coinError.message === 'Insufficient coins') {
            setError('Insufficient coins. Please purchase more coins to enter this tournament.');
          } else {
            throw coinError;
          }
          return;
        }
      }

      // Insert tournament entry
      const { data, error } = await supabase
        .from('tournament_entries')
        .insert({
          tournament_id: tournamentId,
          user_id: user.id,
          status: 'registered'
        })
        .select()
        .single();

      if (error) throw error;

      // Reload entries and tournament to update participant count
      await loadEntries();
      await loadTournament();

      // Auto-regenerate bracket if registration is not locked
      if (tournamentId) {
        await BracketGenerationService.autoRegenerateBracket(tournamentId);
      }
    } catch (error) {
      console.error('Error joining tournament:', error);
      setError('Failed to join tournament. Please try again.');
    } finally {
      setEntryLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).format(date);
  };

  const getTimeRemaining = (endDate: string) => {
    const end = new Date(endDate).getTime();
    const now = new Date().getTime();
    
    const distance = end - now;
    
    if (distance < 0) return 'Tournament has ended';
    
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h remaining`;
  };

  const getRoundLabel = (round: number, total: number) => {
    if (total === 0) return `Round ${round}`;
    if (round === total) return 'Grand Final';
    if (round === total - 1) return 'Semi-Final';
    if (round === total - 2) return 'Quarter-Final';
    return `Round ${round}`;
  };

  const getMatchResult = (match: MatchWithDetails, userId: string): 'win' | 'loss' | 'pending' => {
    if (match.match_status !== 'completed' && match.match_status !== 'forfeited') return 'pending';
    return match.winner_id === userId ? 'win' : 'loss';
  };

  const getUserSideData = (match: MatchWithDetails, userId: string) => {
    const isP1 = match.participant1_id === userId;
    return {
      myProfile: isP1 ? match.participant1 : match.participant2,
      myEntry: isP1 ? match.participant1_entry : match.participant2_entry,
      oppProfile: isP1 ? match.participant2 : match.participant1,
      oppEntry: isP1 ? match.participant2_entry : match.participant1_entry,
    };
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center mb-6">
          <Link to="/tournaments" className="mr-4 text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-2xl font-bold">Tournament Not Found</h1>
        </div>
        
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <AlertTriangle size={36} className="text-yellow-400" />
          </div>
          <h2 className="text-xl font-bold mb-2">Tournament Not Found</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">
            The tournament you're looking for doesn't exist or may have been removed.
          </p>
          <Link to="/tournaments" className="btn-primary inline-flex items-center">
            <ArrowLeft size={18} className="mr-2" />
            Back to Tournaments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/tournaments" className="mr-4 text-white/70 hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-bold">Tournament Details</h1>
      </div>
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center text-red-500">
          <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 flex items-center text-green-500">
          <CheckCircle size={20} className="mr-2 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {pendingInvitation && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Mail size={20} className="text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-300 mb-1">You're Invited!</h3>
              <p className="text-white/70 text-sm mb-4">
                You've been invited to join this tournament. Accept the invitation to participate.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleInvitationResponse('accepted')}
                  disabled={invitationResponding}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Check size={16} />
                  Accept Invitation
                </button>
                <button
                  onClick={() => handleInvitationResponse('declined')}
                  disabled={invitationResponding}
                  className="flex items-center gap-1.5 bg-kod-purple-600 hover:bg-kod-purple-500 text-white/80 text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  <XMarkIcon size={16} />
                  Decline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentMatch && currentMatch.submission_deadline && !userEliminated && tournament?.status === 'active' && (() => {
        const { urgency } = MatchProgressionService.getDeadlineUrgency(currentMatch);
        const statusDisplay = MatchProgressionService.getMatchStatusDisplay(currentMatch, user?.id);
        const opponentName = currentMatch.participant1_id === user?.id
          ? currentMatch.participant2?.username
          : currentMatch.participant1?.username;

        return (
          <div className="mb-6">
            <MatchCountdown
              deadline={currentMatch.submission_deadline!}
              urgency={urgency}
              userSubmitted={statusDisplay.userSubmitted}
              opponentName={opponentName}
              roundNumber={currentMatch.round_number}
            />
          </div>
        );
      })()}

      {/* Match Journey Panel — shown only after registration closes and bracket is official */}
      {user && userEntry && userMatches.length > 0 && isRegistrationClosed(tournament) && (
        <div className="mb-6">
          {/* Header row */}
          <div className="flex items-center gap-3 mb-3">
            <Swords size={18} className="text-kod-accent" />
            <h2 className="text-base font-semibold tracking-wide uppercase text-white/70">Your Tournament Journey</h2>
            <div className="flex-1 h-px bg-white/10" />
            {userEliminated ? (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-red-400">
                <XCircle size={15} /> Eliminated
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-green-400">
                <TrendingUp size={15} /> Still In
              </span>
            )}
          </div>

          {/* Match cards — one per round played/current */}
          <div className="space-y-3">
            {[...userMatches].reverse().map(match => {
              const result = getMatchResult(match, user.id);
              const { myProfile, myEntry, oppProfile, oppEntry } = getUserSideData(match, user.id);
              const isCurrentActiveMatch = currentMatch?.id === match.id;
              const roundLabel = getRoundLabel(match.round_number, bracketTotalRounds);

              const borderClass = result === 'win'
                ? 'border-green-500/40'
                : result === 'loss'
                ? 'border-red-500/40'
                : isCurrentActiveMatch
                ? 'border-kod-accent/60 ring-1 ring-kod-accent/20'
                : 'border-white/10';

              const bgClass = result === 'win'
                ? 'bg-green-500/5'
                : result === 'loss'
                ? 'bg-red-500/5'
                : isCurrentActiveMatch
                ? 'bg-kod-accent/5'
                : 'bg-white/3';

              const isP1 = match.participant1_id === user.id;
              const isForfeitNoSubmission = match.match_status === 'forfeited' && (match.forfeit_reason === 'timeout_both_no_submission' || match.forfeit_reason === 'timeout_one_submission');

              const getRoundScore = (userId: string | null | undefined, round: number): number | null => {
                if (!userId) return null;
                const rp = roundPerformances.find(r => r.user_id === userId && r.tournament_round === round);
                if (!rp) return null;
                const perf = Array.isArray(rp.performance) ? rp.performance[0] : null;
                if (!perf) return null;
                const analysisScore = Array.isArray((perf as any).analysis) && (perf as any).analysis[0]?.overall_score != null
                  ? Math.round((perf as any).analysis[0].overall_score)
                  : null;
                return analysisScore ?? perf?.score ?? null;
              };

              const myRoundScore = getRoundScore(isP1 ? match.participant1_id : match.participant2_id, match.round_number);
              const oppRoundScore = getRoundScore(isP1 ? match.participant2_id : match.participant1_id, match.round_number);
              const myDidNotSubmit = isForfeitNoSubmission && myRoundScore === null;
              const oppDidNotSubmit = isForfeitNoSubmission && oppRoundScore === null;
              const myScore = myDidNotSubmit ? null : myRoundScore;
              const oppScore = oppDidNotSubmit ? null : oppRoundScore;
              const iWon = result === 'win';
              const iLost = result === 'loss';

              return (
                <div
                  key={match.id}
                  className={`rounded-xl border p-4 ${borderClass} ${bgClass} transition-all`}
                >
                  {/* Round label + status pill */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {match.round_number === bracketTotalRounds ? (
                        <Crown size={15} className="text-yellow-400" />
                      ) : (
                        <Swords size={14} className="text-white/40" />
                      )}
                      <span className="font-semibold text-sm">{roundLabel}</span>
                      {isCurrentActiveMatch && (
                        <span className="text-xs bg-kod-accent/20 text-kod-accent border border-kod-accent/30 px-2 py-0.5 rounded-full font-medium">
                          Active Match
                        </span>
                      )}
                    </div>
                    {result === 'win' && (
                      <span className="flex items-center gap-1 text-xs font-bold text-green-400 bg-green-500/15 border border-green-500/30 px-2.5 py-1 rounded-full">
                        <CheckCircle size={11} /> Victory
                      </span>
                    )}
                    {result === 'loss' && (
                      <span className="text-xs font-medium text-red-400/70">
                        Defeated
                      </span>
                    )}
                    {result === 'pending' && !isCurrentActiveMatch && (
                      <span className="flex items-center gap-1 text-xs font-medium text-white/40 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                        <Clock size={11} /> Pending
                      </span>
                    )}
                    {result === 'pending' && isCurrentActiveMatch && (
                      <span className="flex items-center gap-1 text-xs font-medium text-kod-accent bg-kod-accent/10 border border-kod-accent/20 px-2.5 py-1 rounded-full">
                        <Activity size={11} /> In Progress
                      </span>
                    )}
                  </div>

                  {/* Versus row */}
                  <div className="flex items-center gap-3">
                    {/* My side */}
                    <div className={`flex-1 flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                      iWon ? 'bg-green-500/10 border border-green-500/25' : 'bg-white/5 border border-white/8'
                    }`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        iWon ? 'bg-green-600/40 text-green-300' : iLost ? 'bg-white/10 text-white/50' : 'bg-kod-accent/30 text-white'
                      }`}>
                        {(myProfile?.username || 'Me').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-white truncate">
                          {myProfile?.username || 'You'}
                          <span className="ml-1.5 text-xs text-white/30 font-normal">(you)</span>
                        </p>
                        {myScore != null ? (
                          <p className={`text-xl font-bold leading-tight ${iWon ? 'text-green-400' : iLost ? 'text-red-400' : 'text-white'}`}>
                            {myScore}
                          </p>
                        ) : (
                          <p className="text-xs text-white/30 mt-0.5">
                            {match.match_status === 'completed' ? 'No score' : 'Awaiting submission'}
                          </p>
                        )}
                      </div>
                      {iWon && match.winner_id && <Trophy size={18} className="text-yellow-400 flex-shrink-0" />}
                    </div>

                    {/* VS divider */}
                    <div className="flex-shrink-0 text-center">
                      <div className="text-xs font-bold text-white/25 bg-white/5 rounded-full w-8 h-8 flex items-center justify-center mx-auto">
                        VS
                      </div>
                    </div>

                    {/* Opponent side */}
                    <div className={`flex-1 flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                      iLost && match.winner_id ? 'bg-green-500/10 border border-green-500/25' : 'bg-white/5 border border-white/8'
                    }`}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-white/10 text-white/60">
                        {(oppProfile?.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-white/80 truncate">
                          {oppProfile?.username || 'TBD'}
                        </p>
                        {oppScore != null ? (
                          <p className={`text-xl font-bold leading-tight ${iLost && match.winner_id ? 'text-green-400' : 'text-white/50'}`}>
                            {oppScore}
                          </p>
                        ) : (
                          <p className="text-xs text-white/30 mt-0.5">
                            {match.match_status === 'completed' ? 'No score' : 'Awaiting submission'}
                          </p>
                        )}
                      </div>
                      {iLost && match.winner_id && <Shield size={16} className="text-white/30 flex-shrink-0" />}
                    </div>
                  </div>

                  {/* Forfeit note */}
                  {match.match_status === 'forfeited' && (
                    <p className="mt-2 text-xs text-amber-400/80 flex items-center gap-1.5">
                      <AlertTriangle size={11} />
                      {match.forfeit_reason === 'timeout_both_no_submission'
                        ? 'Match decided by forfeit — neither player submitted in time.'
                        : match.forfeit_reason === 'timeout_one_submission'
                        ? 'Match decided by forfeit — opponent did not submit in time.'
                        : 'Match decided by forfeit.'}
                    </p>
                  )}

                  {/* Advancement note */}
                  {result === 'win' && (
                    <p className="mt-2 text-xs text-green-400/70 flex items-center gap-1.5">
                      <ChevronRight size={12} />
                      Advanced to {getRoundLabel(match.round_number + 1, bracketTotalRounds)}
                    </p>
                  )}

                  {/* Upcoming match note */}
                  {result === 'pending' && isCurrentActiveMatch && oppProfile && (
                    <p className="mt-2 text-xs text-white/50 flex items-center gap-1.5">
                      <Mic size={12} />
                      Submit your performance to compete against {oppProfile.username}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Champion banner */}
          {!userEliminated && bracketTotalRounds > 0 && userCurrentRound > bracketTotalRounds && (
            <div className="mt-3 bg-gradient-to-r from-yellow-500/20 via-amber-500/20 to-yellow-500/20 border border-yellow-500/40 rounded-xl p-5 text-center">
              <Crown size={36} className="text-yellow-400 mx-auto mb-2" />
              <h3 className="text-xl font-bold text-yellow-300">Tournament Champion!</h3>
              <p className="text-white/60 text-sm mt-1">You won every match and claimed the title.</p>
            </div>
          )}
        </div>
      )}

      {/* Tournament Header */}
      <div className="card mb-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              {tournament.status === 'active' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-900/30 text-green-400 mr-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1"></div>
                  Active
                </span>
              ) : tournament.status === 'upcoming' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-900/30 text-blue-400 mr-2">
                  Upcoming
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-900/30 text-gray-400 mr-2">
                  Completed
                </span>
              )}
              <span className="text-sm text-white/70">
                {formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}
              </span>
            </div>
            
            <h2 className="text-3xl font-bold mb-3">{tournament.title}</h2>
            <p className="text-white/80 mb-6">{tournament.description}</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div>
                <div className="text-white/60 text-sm flex items-center mb-1">
                  <Trophy size={14} className="mr-1" />
                  Prize
                </div>
                <div className="font-medium">
                  {tournament.prize_pool_amount 
                    ? `${tournament.prize_pool_currency || '$'}${tournament.prize_pool_amount}` 
                    : tournament.prize_description.substring(0, 25) + (tournament.prize_description.length > 25 ? '...' : '')}
                </div>
              </div>
              
              <div>
                <div className="text-white/60 text-sm flex items-center mb-1">
                  <Users size={14} className="mr-1" />
                  Participants
                </div>
                <div className="font-medium">
                  {tournament.participant_count || 0}
                  {tournament.max_participants ? ` / ${tournament.max_participants}` : ''}
                </div>
              </div>
              
              <div>
                <div className="text-white/60 text-sm flex items-center mb-1">
                  <Clock size={14} className="mr-1" />
                  Status
                </div>
                <div className="font-medium">
                  {tournament.status === 'active' 
                    ? getTimeRemaining(tournament.end_date)
                    : tournament.status === 'upcoming'
                    ? `Starts ${formatDate(tournament.start_date)}`
                    : 'Completed'}
                </div>
              </div>
              
              <div>
                <div className="text-white/60 text-sm flex items-center mb-1">
                  <Music size={14} className="mr-1" />
                  Songs
                </div>
                <div className="font-medium">
                  {tournament.songs?.length || 0} songs
                </div>
              </div>
            </div>
            
            {/* Tournament Rules */}
            <div className="mb-6">
              <button 
                className="flex items-center text-white/80 hover:text-white"
                onClick={() => setShowRules(!showRules)}
              >
                {showRules ? (
                  <ChevronUp size={16} className="mr-1" />
                ) : (
                  <ChevronDown size={16} className="mr-1" />
                )}
                Tournament Rules
              </button>
              
              {showRules && (
                <div className="bg-kod-purple-700/50 rounded-lg p-4 mt-2 text-sm">
                  <h4 className="font-semibold mb-2">Rules & Guidelines</h4>
                  {tournament.rules && tournament.rules.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2 text-white/80">
                      {tournament.rules.map((rule, index) => (
                        <li key={index}>{rule}</li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="list-disc list-inside space-y-2 text-white/80">
                      <li>Choose from the available songs and record your performance</li>
                      <li>Only one submission per participant is allowed</li>
                      <li>Your performance will be scored based on pitch accuracy, timing, and overall quality</li>
                      <li>The top 64 performers will advance to the bracket stage</li>
                      <li>Winners will be announced at the end of the tournament period</li>
                      <li>Prize distribution will be handled within 7 days of tournament completion</li>
                      <li>All recordings must comply with platform guidelines</li>
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="lg:w-80">
            {tournament.status === 'active' && (
              <div className="bg-kod-purple-700 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-3">Tournament Status</h3>
                
                {tournament.entry_fee ? (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/70">Entry Fee:</span>
                    <span className="font-semibold">{tournament.entry_fee} Coins</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/70">Entry Fee:</span>
                    <span className="font-semibold">Free</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Participants:</span>
                  <span className="font-semibold">
                    {tournament.participant_count || 0}
                    {tournament.max_participants ? ` / ${tournament.max_participants}` : ''}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Time Remaining:</span>
                  <span className="font-semibold text-kod-accent">
                    {getTimeRemaining(tournament.end_date)}
                  </span>
                </div>
                
                {userEntry ? (
                  <div className="mt-4">
                    <div className="bg-kod-accent/20 border border-kod-accent/40 rounded-lg p-3 mb-4">
                      <div className="flex items-center text-kod-accent mb-1">
                        <CheckCircle size={16} className="mr-1" />
                        <span className="font-semibold">You're registered!</span>
                      </div>
                      <div className="text-sm text-white/80">
                        {!canSubmitRecordings(tournament) ? (
                          tournament.registration_close_date
                            ? `Submissions open on ${new Date(tournament.registration_close_date).toLocaleDateString()}`
                            : 'Waiting for registration to close before submissions open'
                        ) : userEntry.performance_id
                          ? 'You have submitted a performance. You can record more performances until the tournament ends.'
                          : 'Choose a song below and record your performance.'}
                      </div>
                    </div>

                    {canSubmitRecordings(tournament) && (
                      <div className="text-xs text-center text-white/60 mb-2">
                        Select a song from the Tournament Songs section below
                      </div>
                    )}
                  </div>
                ) : isRegistrationClosed(tournament) ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                    <div className="flex items-center text-red-400 mb-1">
                      <AlertTriangle size={16} className="mr-2" />
                      <span className="font-semibold">Registration Closed</span>
                    </div>
                    <p className="text-sm text-white/70">
                      Registration for this tournament has closed. No new participants can join.
                    </p>
                  </div>
                ) : (
                  <button
                    className="btn-primary w-full flex items-center justify-center"
                    onClick={handleJoinTournament}
                    disabled={entryLoading}
                  >
                    {entryLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Joining...
                      </>
                    ) : (
                      <>
                        <Trophy size={18} className="mr-2" />
                        Join Tournament
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {tournament.status === 'upcoming' && (
              <div className="bg-kod-purple-700 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-3">Tournament Status</h3>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Status:</span>
                  <span className="text-blue-400 font-semibold">Upcoming</span>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Starts In:</span>
                  <span className="font-semibold">
                    {getTimeRemaining(tournament.start_date)}
                  </span>
                </div>

                {isRegistrationClosed(tournament) ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                    <div className="flex items-center text-red-400 mb-1">
                      <AlertTriangle size={16} className="mr-2" />
                      <span className="font-semibold">Registration Closed</span>
                    </div>
                    <p className="text-sm text-white/70">
                      Registration for this tournament has closed. No new participants can join.
                    </p>
                  </div>
                ) : tournament.allow_pre_registration ? (
                  userEntry ? (
                    <div className="mt-4">
                      <div className="bg-kod-accent/20 border border-kod-accent/40 rounded-lg p-3 mb-4">
                        <div className="flex items-center text-kod-accent mb-1">
                          <CheckCircle size={16} className="mr-1" />
                          <span className="font-semibold">Pre-Registered</span>
                        </div>
                        <div className="text-sm text-white/80">
                          You've pre-registered! You can record your performance once the tournament starts.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="btn-primary w-full mt-4 flex items-center justify-center"
                      onClick={handleJoinTournament}
                      disabled={entryLoading}
                    >
                      {entryLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Pre-Registering...
                        </>
                      ) : (
                        <>
                          <Trophy size={18} className="mr-2" />
                          Pre-Register for Tournament
                        </>
                      )}
                    </button>
                  )
                ) : (
                  <div className="bg-kod-purple-800/70 rounded-lg p-3 mt-4">
                    <div className="flex items-center mb-1">
                      <Clock4 size={16} className="text-blue-400 mr-2" />
                      <span className="font-semibold">Registration Not Open</span>
                    </div>
                    <p className="text-sm text-white/70">
                      This tournament hasn't started yet. Check back on {formatDate(tournament.start_date)} to join.
                    </p>
                  </div>
                )}
              </div>
            )}

            {tournament.status === 'completed' && (
              <div className="bg-kod-purple-700 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-3">Tournament Status</h3>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Status:</span>
                  <span className="text-white/70 font-semibold">Completed</span>
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white/70">Ended:</span>
                  <span className="font-semibold">
                    {formatDate(tournament.end_date)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/70">Participants:</span>
                  <span className="font-semibold">
                    {tournament.participant_count || 0}
                  </span>
                </div>
                
                {userEntry ? (
                  <div className="bg-kod-purple-800/70 rounded-lg p-3">
                    <div className="flex items-center mb-1">
                      <Star size={16} className="text-yellow-400 mr-2" />
                      <span className="font-semibold">Your Placement: 
                        {entries.findIndex(e => e.id === userEntry.id) + 1}/{entries.length}
                      </span>
                    </div>
                    <p className="text-sm text-white/70">
                      {userEntry.score 
                        ? `Your performance earned a score of ${userEntry.score}` 
                        : 'Your performance was not scored'}
                    </p>
                  </div>
                ) : (
                  <div className="bg-kod-purple-800/70 rounded-lg p-3">
                    <p className="text-sm text-white/70">
                      This tournament has ended. View the results to see the winners.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Tournament Songs */}
      {tournament.songs && tournament.songs.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Tournament Songs</h3>
            {tournament.status === 'active' && !userEntry && (
              <span className="text-sm text-white/60">Join the tournament to record</span>
            )}
            {tournament.status === 'active' && userEntry && !canSubmitRecordings(tournament) && (
              <span className="text-sm text-amber-400">
                {tournament.registration_close_date
                  ? `Recording opens ${new Date(tournament.registration_close_date).toLocaleDateString()}`
                  : 'Waiting for registration to close'}
              </span>
            )}
          </div>
          {userEliminated && userEntry && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-red-400 text-sm">
                You have been eliminated from this tournament. Songs are no longer available to record.
              </p>
            </div>
          )}
          {!userEliminated && userEntry && currentRoundRecordings.length >= 1 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-amber-400 text-sm">
                You've recorded your song for Round {userCurrentRound}. You can record again when the next round opens. Songs you've already sung are locked for the rest of the tournament.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {tournament.songs.map(songEntry => {
              const isUsedSong = songEntry.song && usedSongIds.has(songEntry.song.id);
              const maxRecordingsReached = currentRoundRecordings.length >= 1;
              const isThisRoundSong = !!songEntry.song && currentRoundRecordings.some(r => r.song_id === songEntry.song?.id);
              const songDisabled = userEliminated || isUsedSong || maxRecordingsReached || !canSubmitRecordings(tournament) || !userEntry;

              return songEntry.song && (
                <div
                  key={songEntry.id}
                  className={`bg-kod-purple-700/50 rounded-lg overflow-hidden transition-colors relative ${
                    !songDisabled
                      ? 'hover:bg-kod-purple-700 cursor-pointer'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (!songDisabled) {
                      navigate(`/record/${songEntry.song?.id}?tournament=${tournament.id}&round=${currentMatch?.round_number ?? userCurrentRound}`);
                    }
                  }}
                >
                  {isUsedSong && (
                    <div className="absolute top-2 right-2 bg-red-500/90 text-white text-xs px-2 py-1 rounded">
                      Used
                    </div>
                  )}
                  <img
                    src={songEntry.song.cover_url || 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'}
                    alt={songEntry.song.title}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="p-3">
                    <h4 className="font-medium mb-1">{songEntry.song.title}</h4>
                    <p className="text-sm text-white/70">{songEntry.song.artist}</p>
                    {!songDisabled && (
                      <div className="mt-2 flex justify-end">
                        <button
                          className="flex items-center text-kod-accent text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/record/${songEntry.song?.id}?tournament=${tournament.id}&round=${currentMatch?.round_number ?? userCurrentRound}`);
                          }}
                        >
                          <Mic size={16} className="mr-1" />
                          Record
                        </button>
                      </div>
                    )}
                    {isUsedSong && (
                      <div className="mt-2 text-xs text-red-400">
                        {isThisRoundSong ? `Recorded for Round ${userCurrentRound}` : 'Already used in a previous round'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Tabs for Bracket, Leaderboard, and My Entries - Only show if user has joined */}
      {userEntry && (
        <div className="card mb-6">
          <div className="flex border-b border-kod-purple-600 mb-4">
            <button
              className={`px-4 py-2 font-medium ${
                currentView === 'participants'
                  ? 'text-kod-accent border-b-2 border-kod-accent -mb-px'
                  : 'text-white/70 hover:text-white'
              }`}
              onClick={() => setCurrentView('participants')}
            >
              Participants
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                currentView === 'bracket'
                  ? 'text-kod-accent border-b-2 border-kod-accent -mb-px'
                  : 'text-white/70 hover:text-white'
              }`}
              onClick={() => setCurrentView('bracket')}
            >
              Tournament Bracket
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                currentView === 'leaderboard'
                  ? 'text-kod-accent border-b-2 border-kod-accent -mb-px'
                  : 'text-white/70 hover:text-white'
              }`}
              onClick={() => setCurrentView('leaderboard')}
            >
              Leaderboard
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                currentView === 'myEntries'
                  ? 'text-kod-accent border-b-2 border-kod-accent -mb-px'
                  : 'text-white/70 hover:text-white'
              }`}
              onClick={() => setCurrentView('myEntries')}
            >
              My Entries
            </button>
          </div>

          {currentView === 'participants' ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-white/50">{entries.length} participant{entries.length !== 1 ? 's' : ''}</p>
              </div>
              {entries.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  <Users size={36} className="mx-auto mb-3 opacity-50" />
                  <p>No participants yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center gap-3 bg-kod-purple-700/40 rounded-lg px-4 py-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center text-sm font-bold text-white/70 flex-shrink-0">
                        {(entry.profile?.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium truncate">
                        @{entry.profile?.username || 'unknown'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : currentView === 'bracket' ? (
            <TournamentBracket
              tournamentId={tournament.id}
              tournamentStatus={tournament.status}
              currentUserId={user?.id}
              registrationCloseDate={tournament.registration_close_date}
              registrationLocked={tournament.registration_locked}
              primaryColor={tournament.primary_color || undefined}
              secondaryColor={tournament.secondary_color || undefined}
              title={tournament.title}
              logoUrl={tournament.logo_url}
              prizeDisplay={tournament.prize_display}
              backgroundType={tournament.background_type}
              shaderPreset={tournament.shader_preset}
              backgroundImageUrl={tournament.background_image_url}
              sponsorLogos={tournament.sponsor_logos}
              sponsorLinks={tournament.sponsor_links}
            />
          ) : currentView === 'leaderboard' ? (
            <TournamentLeaderboard
              entries={entries}
              currentUserId={user?.id}
              onPlayPerformance={(entry) => setPlayingEntry(entry)}
              totalRounds={bracketTotalRounds}
              roundPerformances={roundPerformances}
            />
          ) : (
            <TournamentEntriesList tournamentId={tournamentId!} />
          )}
        </div>
      )}
      
      {/* Performance Playback Modal */}
      {playingEntry && playingEntry.performance && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg overflow-hidden max-w-2xl w-full">
            <div className="flex items-center justify-between p-4 border-b border-kod-purple-600">
              <div>
                <h3 className="font-medium">
                  {playingEntry.profile?.username || 'User'}'s Performance
                </h3>
                {playingEntry.performance?.created_at && (
                  <p className="text-sm text-white/70">
                    Submitted on {new Date(playingEntry.performance.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => setPlayingEntry(null)}
                className="p-2 hover:bg-kod-purple-700 rounded-full"
              >
                <ChevronDown size={20} />
              </button>
            </div>
            
            <div className="bg-black aspect-video flex items-center justify-center">
              {playingEntry.performance.vocal_recording_url ? (
                <audio 
                  src={playingEntry.performance.vocal_recording_url}
                  controls
                  autoPlay
                  className="w-full max-w-md"
                />
              ) : (
                <div className="text-white/50">No recording available</div>
              )}
            </div>
            
            <div className="p-4 border-t border-kod-purple-600 flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex items-center mr-4">
                  <Activity size={16} className="text-kod-accent mr-2" />
                  <span className="font-semibold">{playingEntry.score || 'Not scored'}</span>
                </div>
                <button className="flex items-center text-white/70 hover:text-white">
                  <Heart size={16} className="mr-1" />
                  Like
                </button>
              </div>
              
              <button
                onClick={() => setPlayingEntry(null)}
                className="btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};